import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import './DeleteDataPage.css';

const PROGRESS_ITEMS = [
  'All completed levels and star ratings',
  'Daily streak count and history',
  'Coins and in-game currency',
  'Unlocked themes and achievements',
  'Spin wheel history',
  'All gameplay statistics',
];

const ACCOUNT_ITEMS = [
  'Your account and login credentials',
  'Email address and profile information',
  'All progress, levels, and game data',
  'Purchase history (in-app purchases are non-refundable)',
  'All data stored on our servers linked to your account',
];

const PROGRESS_STEPS = [
  'Open Lexira and go to your Profile tab',
  'Tap the Settings icon in the top corner',
  'Scroll down and tap "Delete Progress & Data"',
  'Tap Delete to confirm — this cannot be undone',
];

const ACCOUNT_STEPS = [
  'Open Lexira and go to your Profile tab',
  'Tap the Settings icon in the top corner',
  'Scroll down and tap "Delete Account"',
  'Enter your password to verify your identity',
  'Tap "Delete My Account" to permanently close your account',
];

function DeletedItem({ text }) {
  return (
    <li className="dd-deleted-item">
      <svg className="dd-x-icon" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="9" fill="rgba(255,80,80,0.15)" />
        <path d="M5.5 5.5l7 7M12.5 5.5l-7 7" stroke="#ff7070" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      <span>{text}</span>
    </li>
  );
}

function StepItem({ number, text, isLast }) {
  return (
    <div className="dd-step-item">
      <div className="dd-step-left">
        <div className="dd-step-circle">{number}</div>
        {!isLast && <div className="dd-step-line" />}
      </div>
      <p className="dd-step-text">{text}</p>
    </div>
  );
}

export default function DeleteDataPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="dd-page">
      <div className="dd-bg-glow" />

      <div className="dd-wrap">
        <Link to="/" className="legal-back">← Back to Home</Link>

        {/* ── Hero ─────────────────────────────────────────────── */}
        <div className="dd-hero">
          <img
            src="/assets/mascot/mascot-reading.svg"
            alt="Lexi mascot"
            className="dd-hero-mascot"
          />
          <div className="dd-app-badge">In-App Settings</div>
          <h1 className="dd-hero-title">Manage Your Data</h1>
          <p className="dd-hero-sub">
            You have full control over your data. Both options are available
            directly inside the <strong>Lexira app</strong> under Settings.
          </p>
        </div>

        <div className="dd-cards">

          {/* ── Card 1: Delete Progress ───────────────────────── */}
          <div className="dd-card dd-card--progress">
            <div className="dd-card-stripe dd-stripe--progress" />
            <div className="dd-card-body">

              <div className="dd-card-top">
                <div className="dd-card-icon-wrap dd-icon--progress">🗑️</div>
                <div>
                  <h2 className="dd-card-title">Delete Progress &amp; Data</h2>
                  <p className="dd-card-sub">
                    Your account stays active — only game data is wiped. Start fresh any time.
                  </p>
                </div>
              </div>

              <div className="dd-section">
                <p className="dd-section-label">What gets deleted</p>
                <ul className="dd-deleted-list">
                  {PROGRESS_ITEMS.map((item) => <DeletedItem key={item} text={item} />)}
                </ul>
              </div>

              <div className="dd-callout dd-callout--amber">
                <span className="dd-callout-icon">⚠️</span>
                <span>
                  This action is <strong>permanent</strong> — deleted progress cannot be
                  restored. Your login and account remain intact.
                </span>
              </div>

              <div className="dd-section dd-section--last">
                <p className="dd-section-label">How to do it in the app</p>
                <div className="dd-steps">
                  {PROGRESS_STEPS.map((step, i) => (
                    <StepItem
                      key={i}
                      number={i + 1}
                      text={step}
                      isLast={i === PROGRESS_STEPS.length - 1}
                    />
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* ── OR divider ───────────────────────────────────── */}
          <div className="dd-divider">
            <div className="dd-divider-line" />
            <span className="dd-divider-text">or</span>
            <div className="dd-divider-line" />
          </div>

          {/* ── Card 2: Delete Account ────────────────────────── */}
          <div className="dd-card dd-card--account">
            <div className="dd-card-stripe dd-stripe--account" />
            <div className="dd-card-body">

              <div className="dd-card-top">
                <div className="dd-card-icon-wrap dd-icon--account">🚫</div>
                <div>
                  <h2 className="dd-card-title dd-title--red">Delete Account</h2>
                  <p className="dd-card-sub">
                    Permanently close your account and erase all associated data from our servers.
                  </p>
                </div>
              </div>

              <div className="dd-section">
                <p className="dd-section-label">What gets deleted</p>
                <ul className="dd-deleted-list">
                  {ACCOUNT_ITEMS.map((item) => <DeletedItem key={item} text={item} />)}
                </ul>
              </div>

              <div className="dd-callout dd-callout--red">
                <span className="dd-callout-icon">🚨</span>
                <span>
                  Deleting your account is <strong>permanent and irreversible</strong>.
                  All data, progress, and purchases will be permanently lost.
                </span>
              </div>

              <div className="dd-section dd-section--last">
                <p className="dd-section-label">How to do it in the app</p>
                <div className="dd-steps">
                  {ACCOUNT_STEPS.map((step, i) => (
                    <StepItem
                      key={i}
                      number={i + 1}
                      text={step}
                      isLast={i === ACCOUNT_STEPS.length - 1}
                    />
                  ))}
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* ── Footer help card ─────────────────────────────────── */}
        <div className="dd-footer-card">
          <p className="dd-footer-title">Need help?</p>
          <p className="dd-footer-sub">
            Can't find the option in the app, or want to request deletion by email?
          </p>
          <div className="dd-footer-links">
            <a href="mailto:privacy@lexira.app" className="dd-footer-btn dd-footer-btn--outline">
              Email privacy@lexira.app
            </a>
            <Link to="/support" className="dd-footer-btn dd-footer-btn--solid">
              Open Support →
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
